import React, { useEffect, useState, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HCDataGrouping from "highcharts/modules/data";
import HCBoost from "highcharts/modules/boost";
import { lightenHexToRgba } from "../../../components/Common/Utils/Utils";
import dayjs from "dayjs";
import { useAuthContext } from "../../../context/AuthProvider";

// Initialize data grouping module
HCDataGrouping(Highcharts);
HCBoost(Highcharts);

const OverlayGraph = ({
  selectedGranularity,
  newData,
  scrapDate,
  showScrapPart,
  categories = [],
}) => {
  const [state, setState] = useState(null);
  const chartRef = useRef(null);
  const { QCFull } = useAuthContext();

  const scrapDatesUnix = scrapDate?.map((date) => dayjs(date).unix() * 1000);

  function getIndicesMultiple(array) {
    const count = Math.floor(array.length / 7);
    if (count === 0) return [0];

    return array
      .map((_, index) => index)
      .filter((index) => index % count === 0)
      .slice(0, 7);
  }

  const plotLines = scrapDate?.map((time, index) => ({
    color: "#9f9f9f",
    value: categories.indexOf(time),
    width: 1,
    zIndex: 1,
  }));

  const timezoneOffset = new Date().getTimezoneOffset();

  const timeFlag = () => {
    if (selectedGranularity === "Daily" || selectedGranularity === "Monthly") {
      return true;
    } else if (newData?.[0]?.actualValues?.length > 1200) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    let initialMin, initialMax;
    setState({
      chart: {
        type: "line",
        height: QCFull
          ? window.screen.height - window.screen.height / 3
          : window.screen.height - window.screen.height / 2.1 || "450px",
        toolbar: {
          show: false,
        },
        zoomType: "x",
        zoomKey: "ctrl",
        panning: {
          enabled: true,
          type: "x",
        },
        zooming: {
          mouseWheel: {
            enabled: false,
          },
        },
        panKey: "shift",
        // events: {
        //   load: function () {
        //     const chart = this;
        //     chartRef.current = chart.container;

        //     const xAxis = chart.xAxis[0];
        //     initialMin = xAxis.min;
        //     initialMax = xAxis.max;

        //     chartRef.current.addEventListener("wheel", (event) => {
        //       event.preventDefault();
        //       const delta = event.deltaY || event.detail || event.wheelDelta;
        //       const xAxis = chart.xAxis[0];
        //       const extremes = xAxis.getExtremes();
        //       const range = extremes.max - extremes.min;
        //       const zoomRatio = delta > 0 ? 1.2 : 0.8; // Adjust the ratio for more or less zoom

        //       let newMin = extremes.min + (range * (1 - zoomRatio)) / 2;
        //       let newMax = extremes.max - (range * (1 - zoomRatio)) / 2;

        //       // Ensure the new range does not exceed initial range
        //       if (newMin < initialMin) newMin = initialMin;
        //       if (newMax > initialMax) newMax = initialMax;

        //       xAxis.setExtremes(newMin, newMax);
        //     });
        //   },
        // },
      },
      boost: {
        seriesThreshold: 100000,
      },
      credits: {
        enabled: false,
      },
      tooltip: {
        shared: true,
        crosshairs: true,
        style: {
          fontSize: "11px",
          fontWeight: "600",
        },
        formatter: function () {
          const xAxisLabel = dayjs(this.x).format(
            selectedGranularity === "Minute" || selectedGranularity === "Hourly"
              ? "YYYY-MM-DD HH:mm:ss"
              : "YYYY-MM-DD HH:mm"
          );
          let tooltipText = `<p style="font-size: 10px;">${xAxisLabel}</p><br/>`;
          this.points.forEach((point) => {
            tooltipText += `<span style="color:${
              point.series.color
            }">\u25CF</span> ${point.series.name}: <b>${point.y.toFixed(
              2
            )}</b><br/>`;
          });
          const scrapDatesInRange = scrapDatesUnix.filter(
            (date) =>
              Math.abs(date - dayjs(this.x).unix() * 1000) < 30 * 60 * 1000
          );
          if (scrapDatesInRange.length > 0 && showScrapPart) {
            tooltipText += `<br/><span style="color: #EF7B64;">Scrap Dates:</span>`;
            scrapDatesInRange.forEach((scrapDate) => {
              tooltipText += `<br/><span style="color: #EF7B64;">${dayjs(
                scrapDate
              ).format("YYYY-MM-DD HH:mm:ss")}</span>`;
            });
          }
          return tooltipText;
        },
      },
      title: {
        text: null,
      },
      plotOptions: {
        series: {
          dataGrouping: {
            enabled: true,
            approximation: "average", // Choose appropriate aggregation function
            groupPixelWidth: 10, // Adjust based on your data density
          },
          connectNulls: true,
        },
      },
      legend: {
        enabled: false,
      },
      time: {
        timezoneOffset: timezoneOffset,
      },
      xAxis: {
        type: "category",
        categories: categories,
        labels: {
          style: {
            color: "#404040",
            fontSize: "10px",
            fontWeight: "600",
          },
          formatter: function () {
            return dayjs(this.value).format(
              timeFlag() ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD HH:mm:ss"
            );
          },
        },
        tickPositions: getIndicesMultiple(categories),
        lineWidth: 0,
        gridLineWidth: 0,
        plotLines: showScrapPart ? plotLines : [],
        dateTimeLabelFormats: {
          second: "%Y-%m-%d %H:%M:%S",
          minute: "%Y-%m-%d %H:%M:%S",
          hour: "%Y-%m-%d %H:%M:%S",
          day: timeFlag() ? "%Y-%m-%d %H:%M" : "%Y-%m-%d %H:%M:%S",
          week: timeFlag() ? "%Y-%m-%d %H:%M" : "%Y-%m-%d %H:%M:%S",
          month: timeFlag() ? "%Y-%m-%d %H:%M" : "%Y-%m-%d %H:%M:%S",
          year: "%Y-%m-%d",
        },
      },
      yAxis: newData?.map((dataSet, index) => ({
        title: {
          text: "",
        },
        labels: {
          format: "{value}",
          style: {
            color: dataSet.color,
            fontSize: "10px",
            fontWeight: "600",
          },
          formatter: function () {
            if (this.value >= 1000) {
              return this.value / 1000 + "k";
            } else {
              return this.value;
            }
          },
          distance: 10,
        },
        gridLineWidth: 0,
        lineWidth: 1,
        margin: 12,
        tickPlacement: "on",
        tickWidth: 1,
        tickLength: 8,
        lineColor: dataSet.color,
        plotBands: [
          {
            from: dataSet.lowerLimit,
            to: dataSet.upperlimit,
            color: lightenHexToRgba(dataSet.color),
            zIndex: 0,
          },
        ],
        opposite: false,
      })),
      series: newData?.flatMap((dataSet, index) => [
        {
          name: `Actual Value ${dataSet.factorName}`,
          data: dataSet.actualValues ? dataSet.actualValues : [],
          type: "line",
          color: dataSet.color,
          marker: {
            enabled: false,
          },
          yAxis: index,
          connectNulls: true,
          turboThreshold: 0,
        },
        {
          name: `Mean ${dataSet.factorName}`,
          data: dataSet.averageValues ? dataSet.averageValues : [],
          color: dataSet.color,
          marker: {
            enabled: false,
          },
          yAxis: index,
          dashStyle: "Dash",
          turboThreshold: 0,
        },
      ]),
    });
  }, [
    window.screen.height,
    newData,
    scrapDate,
    showScrapPart,
    selectedGranularity,
    QCFull,
  ]);

  if (!newData) {
    return null; // or render a loading indicator
  }

  return (
    <div id="chart" style={{ borderRadius: "16px" }}>
      <HighchartsReact highcharts={Highcharts} options={state} />
    </div>
  );
};
export default OverlayGraph;
